import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import { agentsData } from "Stores/agentsData";
import deepResearchStore from "Stores/deepResearchData";
import { makeApi, URLS } from "Utils/apiURL";
import AgentSelect from "./AgentSelect";
import { DEFAULT_DAYS, FREQUENCY_OPTIONS, daysOf, defaultTimezone } from "./DayOfWeekField";
import { SetupPage, When, Step, Field, Schedule, SequenceField, NotifyStep, SkipLine, clampInt } from "./SetupFlow";

const DEFAULT_NAME = "Daily ICP Prospector";

const CreateAgent = observer(() => {
  const history = useHistory();
  const [name, setName] = useState(DEFAULT_NAME);
  const [sellerProfileId, setSellerProfileId] = useState("");
  const [sequenceId, setSequenceId] = useState("");
  const [sequenceName, setSequenceName] = useState("");
  const [dailyCap, setDailyCap] = useState(25);
  const [requireApproval, setRequireApproval] = useState(false);
  const [notifyMode, setNotifyMode] = useState("DAILY_DIGEST");
  const [time, setTime] = useState("08:00");
  const [frequency, setFrequency] = useState("DAILY"); // DAILY | WEEKDAYS | WEEKLY
  const [days, setDays] = useState(DEFAULT_DAYS);
  const [fromAI, setFromAI] = useState(false);
  const timezone = defaultTimezone();
  const [saving, setSaving] = useState(false);
  const [website, setWebsite] = useState("");
  const [creatingSeller, setCreatingSeller] = useState(false);
  const [sellerError, setSellerError] = useState("");
  const [showCreateSeller, setShowCreateSeller] = useState(false);

  useEffect(() => {
    deepResearchStore.fetchSellerProfiles({ force: true });
    // Arrived from "Describe it": prefill what the description said (schedule, limits, approval).
    const draft = agentsData.consumeTemplateDraft("daily_icp_prospector");
    if (draft) {
      setFromAI(true);
      if (draft.name) setName(draft.name);
      const tc = (draft.trigger && draft.trigger.config) || {};
      if (["DAILY", "WEEKDAYS", "WEEKLY"].includes(tc.frequency)) setFrequency(tc.frequency);
      if (tc.time) setTime(tc.time);
      if (tc.frequency === "WEEKLY") setDays(daysOf(tc));
      const c = draft.config || {};
      if (c.dailyRecordCap) setDailyCap(c.dailyRecordCap);
      if (c.notifyMode) setNotifyMode(c.notifyMode);
      if (c.approvalMode === "REQUIRED") setRequireApproval(true);
    }
  }, []);

  const sellers = deepResearchStore.sellerProfiles || [];
  const canSubmit = !!sellerProfileId && !!sequenceId;

  const createSeller = async () => {
    const site = website.trim();
    if (!site) return;
    setCreatingSeller(true);
    setSellerError("");
    const res = await makeApi({
      url: URLS.deepResearchEnrichSeller,
      method: "POST",
      data: { website: site }
    });
    if (res && res.data && !res.data.error) {
      const saved = await deepResearchStore.saveSellerProfile({ ...res.data, website: site });
      if (saved && saved.id) {
        setSellerProfileId(saved.id);
        setWebsite("");
        setShowCreateSeller(false);
      } else {
        setSellerError("Could not save the profile. Try again.");
      }
    } else {
      setSellerError("Couldn't build a profile from that website. Check the URL and try again.");
    }
    setCreatingSeller(false);
  };

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    const agent = await agentsData.createDailyIcpProspector({
      name: name.trim() || DEFAULT_NAME, sellerProfileId, sequenceId, dailyCap: clampInt(dailyCap, 1, 200, 25), time, timezone,
      frequency, ...(frequency === "WEEKLY" ? { days } : {}),
      approvalMode: requireApproval ? "REQUIRED" : "NONE",
      notifyMode
    });
    setSaving(false);
    if (agent && agent.id) history.push(`/agents/${agent.id}`);
  };

  const sellerOptions = sellers.map(s => {
    const base = s.company_name || s.companyName || s.product_name || s.name || s.website || s.id;
    const sub = s.product_name && s.product_name !== base ? ` · ${s.product_name}` : "";
    return { value: s.id, label: `${base}${sub}` };
  });
  const creatingOpen = showCreateSeller || (sellers.length === 0 && !deepResearchStore.sellerProfilesLoading);
  const cap = clampInt(dailyCap, 1, 200, 25);

  return (
    <SetupPage
      title="Hire · Daily ICP Prospector"
      sub={fromAI ? "Set up from your description. Pick a seller profile and a sequence, then create." : null}
      name={{ value: name, onChange: setName, placeholder: DEFAULT_NAME }}
      bar={{ createLabel: "Create & build ICP",
        canSubmit, saving, onCreate: submit, onCancel: () => history.push("/agents"),
        cost: `About 1 credit per contact · up to ${cap} a run`,
        reason: !sellerProfileId && !sequenceId ? "Pick a seller profile and a sequence to create it."
          : !sellerProfileId ? "Pick a seller profile to create it." : "Pick a sequence to create it."
      }}>
      <When icon="clock">
        <Schedule runWhen={frequency} setRunWhen={setFrequency} days={days} setDays={setDays} time={time} setTime={setTime} timezone={timezone} manual={false} options={FREQUENCY_OPTIONS} />
      </When>
      <Step icon="user" tone="violet" title="Find people who fit your ICP">
        {sellers.length > 0 && (
          <Field hint="After you create it, you set who to find in List Builder, starting from this profile.">
            <div className={sellerProfileId ? "" : "ag-sf__needwrap"}>
            <AgentSelect value={sellerProfileId} placeholder="Pick a seller profile" searchable searchPlaceholder="Search seller profiles…"
              onChange={setSellerProfileId} options={sellerOptions} />
            </div>
          </Field>
        )}
        {sellers.length === 0 && deepResearchStore.sellerProfilesLoading && <span className="ag-sf__hint">Loading seller profiles…</span>}
        {sellers.length > 0 && !showCreateSeller && (
          <button type="button" className="ag-link ag-sf__clear" onClick={() => setShowCreateSeller(true)}>
            <i className="material-icons-outlined">add</i>Create a new seller profile
          </button>
        )}
        {creatingOpen && (
          <Field label="Your company website" hint={creatingSeller ? "Building your profile. This takes about 20s." : sellerError || null} warn={!!sellerError && !creatingSeller}>
            <div className="ag-sf__inline">
              <input className="ag-input" placeholder="https://yourcompany.com" value={website} onChange={e => setWebsite(e.target.value)} disabled={creatingSeller} />
              <button className="ag-btn ag-btn--primary" disabled={creatingSeller || !website.trim()} onClick={createSeller}>
                {creatingSeller ? "Building…" : "Create profile"}
              </button>
              {sellers.length > 0 && !creatingSeller && (
                <button type="button" className="ag-btn" onClick={() => { setShowCreateSeller(false); setSellerError(""); setWebsite(""); }}>Cancel</button>
              )}
            </div>
          </Field>
        )}
        <Field label="Contacts per run">
          <input className="ag-input ag-sf__num" type="number" min="1" max="200" value={dailyCap} onChange={e => setDailyCap(e.target.value)} />
        </Field>
      </Step>
      <Step icon="mail" tone="violet" title="Find their emails" tags={[{ label: "1 credit each" }]} />
      <Step icon="send" tone="amber" title="Add to a sequence" tags={[requireApproval && { label: "Asks you first", ask: true }]}>
        <SequenceField id={sequenceId} name={sequenceName} onPick={s => { setSequenceId(s.id); setSequenceName(s.name); }} />
        <label className="ag-sf__check">
          <input type="checkbox" checked={requireApproval} onChange={e => setRequireApproval(e.target.checked)} />
          <span>Ask me before enrolling each run</span>
        </label>
      </Step>
      <NotifyStep value={notifyMode} onChange={setNotifyMode} />
      <SkipLine>Skips anyone already contacted or on do-not-contact.</SkipLine>
    </SetupPage>
  );
});

export default CreateAgent;
